import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

const PALETTE = [
  "hsl(161 94% 30%)",   // emerald
  "hsl(217 91% 60%)",   // blue
  "hsl(262 60% 55%)",   // violet
  "hsl(30 90% 55%)",    // amber
  "hsl(340 75% 55%)",   // rose
  "hsl(180 70% 40%)",   // teal
  "hsl(45 85% 50%)",    // yellow
];

const TYPE_LABELS = {
  founder: "Founders",
  employee: "Employees",
  investor: "Investors",
  option_pool: "Option Pool",
  shareholder: "Shareholders",
};

export default function CapTableChart({ entries = [] }) {
  if (!entries || entries.length === 0) {
    return (
      <div className="rounded-2xl border border-border bg-card p-12 text-center text-muted-foreground italic">
        Cap table not disclosed.
      </div>
    );
  }

  const data = entries.map((e, i) => ({
    name: e.name,
    value: Number(e.percentage) || 0,
    type: e.type,
    fill: PALETTE[i % PALETTE.length],
  }));

  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
      <div className="lg:col-span-2 relative">
        <div className="h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={80}
                outerRadius={130}
                paddingAngle={2}
                strokeWidth={0}
              >
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                formatter={(v, n) => [`${v}%`, n]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Ownership</div>
            <div className="font-display font-bold text-3xl num-display">{total}%</div>
          </div>
        </div>
      </div>
      <div className="lg:col-span-3">
        <div className="space-y-3">
          {data.map((entry, i) => (
            <div key={i} className="flex items-center gap-3 group" data-testid={`cap-table-entry-${i}`}>
              <div className="h-3 w-3 rounded-sm shrink-0" style={{ background: entry.fill }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium truncate">{entry.name}</div>
                  <div className="num-display font-display text-base font-semibold">{entry.value}%</div>
                </div>
                <div className="mt-1 h-1.5 w-full bg-secondary rounded-full overflow-hidden">
                  <div className="h-full transition-all duration-500 group-hover:brightness-110" style={{ width: `${entry.value}%`, background: entry.fill }} />
                </div>
                <div className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{TYPE_LABELS[entry.type] || entry.type}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
